/* ================================================================
   MPS — STRUCTURAL AGING SCORE QUIZ v2
   Data-driven engine with branching, vanilla JS, zero dependencies.
================================================================ */

(function () {
  'use strict';

  /* ── i18n bootstrap ───────────────────────────────────────────
     Reads window.__quizI18n (set by quiz-i18n.js loaded before this file).
     Falls back to 'fr' if the current locale has no entry.
  ─────────────────────────────────────────────────────────────── */
  var _lang = (document.documentElement.lang || 'fr').split('-')[0];
  var _t    = (window.__quizI18n && (window.__quizI18n[_lang] || window.__quizI18n['fr'])) || {};
  function _ui(key) { return (_t.ui && _t.ui[key]) || ''; }

  var GSHEET_WEBHOOK_URL = 'https://script.google.com/macros/s/AKfycbwsAZEPSz9J4URkxPteTDRjhvDpLZYvgyqzy-EZq00y03uxSJ0uPzB3wsfsgmOzEwJwRg/exec'; // Coller l'URL Google Apps Script ici

  /* =============================================================
     QUIZ NODE DEFINITIONS
  ============================================================= */

  const NODES = [
    // ── INTRO ──────────────────────────────────────────────────
    {
      id: 'welcome',
      type: 'intro',
      next: 'prenom',
    },
    {
      id: 'prenom',
      type: 'text',
      question: 'Comment vous<br>appelez-vous\u00a0?',
      placeholder: 'Votre prénom',
      stateKey: 'prenom',
      required: true,
      next: 'consent',
    },
    {
      id: 'consent',
      type: 'consent',
      next: 'trans_profil',
    },
    // ── PROFIL ─────────────────────────────────────────────────
    {
      id: 'trans_profil',
      type: 'transition',
      eyebrow: 'Étape 1 — Profil',
      title: 'Commençons par<br>votre profil.',
      subtitle: 'Ces informations nous permettent de calculer votre score de vieillissement biologique de référence.',
      next: 'age',
    },
    {
      id: 'age',
      type: 'number',
      question: 'Quel est votre âge\u00a0?',
      stateKey: 'age',
      min: 18, max: 99, unit: 'ans',
      required: true,
      next: 'genre',
    },
    {
      id: 'genre',
      type: 'single',
      question: 'Quel est votre sexe\u00a0?',
      stateKey: 'genre',
      variant: 'genre',
      options: [
        { value: 'femme', label: 'Femme', icon: 'female' },
        { value: 'homme', label: 'Homme', icon: 'male' },
      ],
      required: true,
      next: 'taille',
    },
    {
      id: 'taille',
      type: 'number',
      question: 'Quelle est votre taille\u00a0?',
      stateKey: 'taille',
      min: 100, max: 250, unit: 'cm',
      required: true,
      next: 'poids',
    },
    {
      id: 'poids',
      type: 'number',
      question: 'Quel est votre poids\u00a0?',
      stateKey: 'poids',
      min: 30, max: 300, unit: 'kg',
      required: true,
      next: 'trans_vie',
    },
    // ── MODE DE VIE ────────────────────────────────────────────
    {
      id: 'trans_vie',
      type: 'transition',
      eyebrow: 'Étape 2 — Mode de vie',
      title: 'Parlons de votre<br>mode de vie.',
      subtitle: 'Ces facteurs influencent directement le rythme du vieillissement biologique.',
      next: 'tabac',
    },
    {
      id: 'tabac',
      type: 'single',
      question: 'Fumez-vous actuellement\u00a0?',
      stateKey: 'tabac',
      options: [
        { value: 'non', label: 'Non' },
        { value: 'occasionnel', label: 'Occasionnellement' },
        { value: 'regulier', label: 'Régulièrement' },
      ],
      required: true,
      next: 'alcool',
    },
    {
      id: 'alcool',
      type: 'single',
      question: "Quelle est votre consommation d'alcool\u00a0?",
      stateKey: 'alcool',
      options: [
        { value: 'rare', label: 'Rare ou jamais' },
        { value: 'modere', label: '1–3 fois par semaine' },
        { value: 'frequent', label: 'Plus fréquemment' },
      ],
      required: true,
      next: 'stress',
    },
    {
      id: 'stress',
      type: 'single',
      question: 'Comment évaluez-vous votre niveau de stress au quotidien\u00a0?',
      stateKey: 'stress',
      options: [
        { value: 'faible', label: 'Faible', sub: 'Je me sens serein(e) au quotidien' },
        { value: 'modere', label: 'Modéré', sub: 'Des pics de stress ponctuels' },
        { value: 'eleve', label: 'Élevé', sub: 'Stress chronique ou fréquent' },
      ],
      whyAsk: {
        title: 'Pourquoi nous demandons cela',
        text: "Le stress chronique accélère le vieillissement cellulaire via l'élévation du cortisol, impactant directement la qualité de la peau et des tissus biologiques.",
      },
      required: true,
      next: 'trans_meno',
    },
    // ── MÉNOPAUSE (conditionnel) ───────────────────────────────
    {
      id: 'trans_meno',
      type: 'transition',
      eyebrow: 'Transitions hormonales',
      title: 'Parlons de votre<br>équilibre hormonal.',
      subtitle: 'Ces informations nous permettent de personnaliser davantage vos recommandations.',
      next: 'meno_concernee',
      skipIf: function (s) { return !(s.genre === 'femme' && s.age >= 45 && s.age < 55); },
    },
    {
      id: 'meno_concernee',
      type: 'single',
      question: 'Êtes-vous concernée par la ménopause\u00a0?',
      stateKey: 'menopause_concernee',
      options: [
        { value: 'oui', label: 'Oui' },
        { value: 'non', label: 'Non' },
        { value: 'ne_sais_pas', label: 'Je ne sais pas' },
      ],
      required: true,
      next: 'meno_stade',
      skipIf: function (s) { return !(s.genre === 'femme' && s.age >= 45 && s.age < 55); },
    },
    {
      id: 'meno_stade',
      type: 'single',
      question: 'À quel stade vous situez-vous aujourd\'hui\u00a0?',
      stateKey: 'menopause_stade',
      options: [
        { value: 'peri', label: 'Péri-ménopause', sub: 'Cycles irréguliers, premiers changements hormonaux' },
        { value: 'pre', label: 'Pré-ménopause', sub: 'Symptômes hormonaux, mais règles encore présentes' },
        { value: 'meno', label: 'Ménopause', sub: 'Arrêt des règles depuis plus de 12 mois' },
        { value: 'post', label: 'Post-ménopause', sub: 'Équilibre hormonal stabilisé après la ménopause' },
      ],
      required: true,
      next: 'trans_objectifs',
      skipIf: function (s) {
        return !(s.genre === 'femme' && s.age >= 45 && s.age < 55 && s.menopause_concernee === 'oui');
      },
    },
    // ── OBJECTIFS ──────────────────────────────────────────────
    {
      id: 'trans_objectifs',
      type: 'transition',
      eyebrow: 'Étape 3 — Objectifs',
      title: "Quels sont vos objectifs d'amélioration\u00a0?",
      subtitle: 'Nous personnaliserons nos recommandations en fonction de vos priorités.',
      next: 'objectifs',
    },
    {
      id: 'objectifs',
      type: 'multi',
      question: 'Quels sont vos objectifs prioritaires\u00a0?',
      sub: 'Sélection multiple possible',
      stateKey: 'objectifs',
      options: [
        { value: 'energie', label: 'Énergie & vitalité' },
        { value: 'peau-visage', label: 'Peau du visage' },
        { value: 'peau-corps', label: 'Peau du corps' },
        { value: 'cheveux-ongles', label: 'Cheveux & ongles' },
        { value: 'stress', label: 'Gestion du stress' },
        { value: 'hormones', label: 'Équilibre hormonal' },
        { value: 'sport', label: 'Récupération sportive' },
        { value: 'inflammation', label: "Réduction de l'inflammation" },
        { value: 'poids', label: 'Perte de poids & cellulite' },
        { value: 'vieillissement', label: 'Réduction du vieillissement biologique' },
      ],
      required: true,
      next: 'trans_peau',
    },
    // ── PEAU DU VISAGE ─────────────────────────────────────────
    {
      id: 'trans_peau',
      type: 'transition',
      eyebrow: 'Étape 4 — Peau du visage',
      title: 'Analysons votre peau du visage.',
      subtitle: 'Sur une échelle de 1 à 5, évaluez honnêtement les changements observés sur votre peau.',
      next: 'peau_densite',
    },
    {
      id: 'peau_densite',
      type: 'scale',
      question: 'Ma peau a perdu en densité et en fermeté avec le temps.',
      stateKey: 'peau_densite',
      required: true,
      next: 'peau_marques',
    },
    {
      id: 'peau_marques',
      type: 'scale',
      question: 'Ma peau marque plus facilement qu\'avant (rides, plis, signes de fatigue).',
      stateKey: 'peau_marques',
      required: true,
      next: 'peau_qualite',
    },
    {
      id: 'peau_qualite',
      type: 'scale',
      question: 'La qualité globale de ma peau s\'est dégradée ces dernières années.',
      stateKey: 'peau_qualite',
      required: true,
      photoPrompt: true,
      next: function (state) {
        return state.veut_photo === 'oui' ? 'photo' : 'trans_energie';
      },
    },
    {
      id: 'photo',
      type: 'upload',
      question: 'Analyse visuelle',
      sub: 'Optionnelle mais recommandée',
      stateKey: 'photo',
      required: false,
      whyAsk: {
        title: 'Pourquoi nous demandons cela',
        text: "Cette image est analysée à l'aide d'une intelligence artificielle comparée à nos données cliniques, afin de mieux contextualiser votre score cutané. Aucune donnée n'est utilisée à des fins de diagnostic.",
      },
      next: 'trans_energie',
    },
    // ── ÉNERGIE & RÉCUPÉRATION ─────────────────────────────────
    {
      id: 'trans_energie',
      type: 'transition',
      eyebrow: 'Étape 5 — Énergie & Récupération',
      title: 'Évaluons votre énergie<br>et votre récupération.',
      subtitle: 'Ces indicateurs fonctionnels reflètent la capacité de régénération de votre organisme.',
      next: 'energie_group_a',
    },
    {
      id: 'energie_group_a',
      type: 'scale_group',
      groupTitle: 'Énergie & régénération',
      items: [
        { question: 'Je récupère moins vite qu\'il y a quelques années.', stateKey: 'energie_recup' },
        { question: 'Les périodes de fatigue durent plus longtemps.', stateKey: 'energie_fatigue' },
        { question: 'J\'ai l\'impression que mon corps se régénère plus lentement.', stateKey: 'energie_regen' },
      ],
      required: true,
      next: 'energie_group_b',
    },
    {
      id: 'energie_group_b',
      type: 'scale_group',
      groupTitle: 'Résilience & inflammaging perçu',
      items: [
        { question: 'Je ressens une fatigue de fond persistante.', stateKey: 'energie_fatigue_fond' },
        { question: 'Mon corps est plus sensible au stress qu\'avant.', stateKey: 'energie_stress' },
        { question: 'J\'ai une sensation régulière de rigidité ou de lourdeur corporelle.', stateKey: 'energie_rigidite' },
      ],
      required: true,
      next: 'sport',
    },
    {
      id: 'sport',
      type: 'multi',
      question: 'Quand je fais du sport\u00a0:',
      sub: 'Choix multiples',
      stateKey: 'sport',
      options: [
        { value: 'recup_rapide', label: 'Je récupère rapidement' },
        { value: 'douleurs_articulaires', label: "J'ai souvent des douleurs articulaires" },
        { value: 'douleurs_musculaires', label: "J'ai souvent des douleurs musculaires" },
        { value: 'fatigue_prolongee', label: "Je ressens une fatigue prolongée après l'effort" },
      ],
      required: true,
      next: 'trans_cellulite',
    },
    // ── CELLULITE & COMPOSITION ────────────────────────────────
    {
      id: 'trans_cellulite',
      type: 'transition',
      eyebrow: 'Étape 6 — Composition corporelle',
      title: 'Alimentation et<br>composition corporelle.',
      subtitle: 'Ces éléments complètent votre profil fonctionnel et métabolique.',
      next: 'alimentation',
    },
    {
      id: 'alimentation',
      type: 'yesno',
      question: 'Mon alimentation est globalement équilibrée.',
      stateKey: 'alimentation',
      required: true,
      next: 'fruits_legumes',
    },
    {
      id: 'fruits_legumes',
      type: 'single',
      question: 'Ma consommation de fruits et légumes est en moyenne\u00a0:',
      stateKey: 'fruits_legumes',
      options: [
        { value: 'rare', label: 'Rare ou inexistante' },
        { value: '1-2', label: '1–2 portions par jour' },
        { value: '3+', label: 'Tous les jours (3+ portions)' },
      ],
      required: true,
      next: 'cellulite_hanches',
    },
    {
      id: 'cellulite_hanches',
      type: 'yesno',
      question: 'Présence de cellulite au niveau des hanches\u00a0?',
      stateKey: 'cellulite_hanches',
      required: true,
      next: 'cellulite_jambes',
    },
    {
      id: 'cellulite_jambes',
      type: 'yesno',
      question: 'Cellulite visible au niveau des jambes\u00a0?',
      stateKey: 'cellulite_jambes',
      required: true,
      next: 'email',
    },
    // ── EMAIL CAPTURE ──────────────────────────────────────────
    {
      id: 'email',
      type: 'email',
      question: 'Dernière étape avant vos résultats\u00a0!',
      sub: 'Renseignez votre email pour recevoir vos recommandations personnalisées.',
      stateKey: 'email',
      required: true,
      next: 'loading',
    },
    // ── LOADING & RESULT ───────────────────────────────────────
    {
      id: 'loading',
      type: 'loading',
      next: 'result',
    },
    {
      id: 'result',
      type: 'result',
      next: null,
    },
  ];

  const NODE_MAP = {};
  NODES.forEach(function (n) { NODE_MAP[n.id] = n; });

  /* ── Patch NODES with translated strings ─────────────────────
     Only overwrites text properties; all logic (id, type, stateKey,
     skipIf, next, value…) is left untouched.
  ─────────────────────────────────────────────────────────────── */
  if (_t.nodes) {
    NODES.forEach(function (node) {
      var ni = _t.nodes[node.id];
      if (!ni) return;
      if (ni.question    !== undefined) node.question    = ni.question;
      if (ni.placeholder !== undefined) node.placeholder = ni.placeholder;
      if (ni.eyebrow     !== undefined) node.eyebrow     = ni.eyebrow;
      if (ni.title       !== undefined) node.title       = ni.title;
      if (ni.subtitle    !== undefined) node.subtitle    = ni.subtitle;
      if (ni.sub         !== undefined) node.sub         = ni.sub;
      if (ni.groupTitle  !== undefined) node.groupTitle  = ni.groupTitle;
      if (ni.unit        !== undefined) node.unit        = ni.unit;
      if (ni.options && node.options) {
        ni.options.forEach(function (o, i) {
          if (!node.options[i]) return;
          if (o.label !== undefined) node.options[i].label = o.label;
          if (o.sub   !== undefined) node.options[i].sub   = o.sub;
        });
      }
      if (ni.items && node.items) {
        ni.items.forEach(function (it, i) {
          if (node.items[i] && it.question !== undefined) node.items[i].question = it.question;
        });
      }
      if (ni.whyAsk) {
        node.whyAsk = node.whyAsk || {};
        if (ni.whyAsk.title !== undefined) node.whyAsk.title = ni.whyAsk.title;
        if (ni.whyAsk.text  !== undefined) node.whyAsk.text  = ni.whyAsk.text;
      }
    });
  }

  const PROGRESS_SKIP = ['intro', 'transition', 'loading', 'result', 'consent', 'upload'];

  /* =============================================================
     QUIZ ENGINE
  ============================================================= */

  const QUIZ = {
    currentNodeId: null,
    history: [],
    isAnimating: false,
    _transitionTimer: null,
    _currentSlideEl: null,
    _gsheetSent: false,
    _shopifyLeadSent: false,

    state: {
      prenom: '', genre: '', age: null, taille: null, poids: null,
      tabac: '', alcool: '', stress: '', objectifs: [],
      peau_densite: null, peau_marques: null, peau_qualite: null,
      veut_photo: '', photo: false,
      consent_rgpd: false,
      consent_marketing: false,
      menopause_concernee: '', menopause_stade: '',
      energie_recup: null, energie_fatigue: null, energie_regen: null,
      energie_fatigue_fond: null, energie_stress: null, energie_rigidite: null,
      sport: [],
      alimentation: '', fruits_legumes: '',
      cellulite_hanches: '', cellulite_jambes: '',
      email: '',
      imc: null, score: null, reco: null,
    },

    /* ── INIT ─────────────────────────────────────────────── */
    init: function () {
      this._loadFromStorage();
      this._bindGlobalEvents();
      this._goTo('welcome', false);
    },

    /* ── NAVIGATION ───────────────────────────────────────── */
    next: function () {
      if (this.isAnimating) return;
      this._clearTransitionTimer();

      var node = NODE_MAP[this.currentNodeId];
      if (!node) return;

      if (!this._validate(node)) return;
      this._saveNode(node);

      var nextId = typeof node.next === 'function' ? node.next(this.state) : node.next;
      if (!nextId) return;

      if (node.branches) {
        nextId = this._evalBranch(node.branches);
      }

      while (nextId && NODE_MAP[nextId] && NODE_MAP[nextId].skipIf && NODE_MAP[nextId].skipIf(this.state)) {
        var skipped = NODE_MAP[nextId];
        nextId = typeof skipped.next === 'function' ? skipped.next(this.state) : skipped.next;
      }

      if (!nextId) return;
      this.history.push(this.currentNodeId);
      this._goTo(nextId, true, 'forward');
    },

    back: function () {
      if (this.isAnimating || this.history.length === 0) return;
      this._clearTransitionTimer();
      var prevId = this.history.pop();
      this._goTo(prevId, true, 'back');
    },

    restart: function () {
      this._clearTransitionTimer();
      var fresh = {};
      for (var k in this.state) fresh[k] = Array.isArray(this.state[k]) ? [] : (typeof this.state[k] === 'boolean' ? false : (typeof this.state[k] === 'number' || this.state[k] === null ? null : ''));
      this.state = fresh;
      this.history = [];
      this._clearStorage();
      this._gsheetSent = false;
      this.isAnimating = false;
      this._goTo('welcome', false);
    },

    _goTo: function (nodeId, animate, direction) {
      var node = NODE_MAP[nodeId];
      if (!node) return;

      var wrapper = document.getElementById('mpsSlidesWrapper');
      var oldEl = this._currentSlideEl;
      var newEl = this._render(node);

      if (animate && oldEl) {
        this.isAnimating = true;
        var exitCls = direction === 'forward' ? 'exit-left' : 'exit-right';
        var enterCls = direction === 'forward' ? 'enter-right' : 'enter-left';

        wrapper.appendChild(newEl);
        newEl.getBoundingClientRect();
        oldEl.classList.add(exitCls);
        newEl.classList.add(enterCls);

        var self = this;
        setTimeout(function () {
          if (oldEl.parentNode) oldEl.parentNode.removeChild(oldEl);
          newEl.classList.remove(enterCls);
          newEl.classList.add('active');
          self._currentSlideEl = newEl;
          self.currentNodeId = nodeId;
          self.isAnimating = false;
          self._updateUI();
          self._onEnter(node);
        }, 320);
      } else {
        wrapper.innerHTML = '';
        newEl.classList.add('active');
        wrapper.appendChild(newEl);
        this._currentSlideEl = newEl;
        this.currentNodeId = nodeId;
        this._updateUI();
        this._onEnter(node);
      }
    },

    /* ── RENDER ENGINE ────────────────────────────────────── */
    _render: function (node) {
      var t = node.type;
      if (t === 'intro')       return this._renderIntro(node);
      if (t === 'text')        return this._renderText(node);
      if (t === 'number')      return this._renderNumber(node);
      if (t === 'single')      return this._renderSingle(node);
      if (t === 'multi')       return this._renderMulti(node);
      if (t === 'scale')       return this._renderScale(node);
      if (t === 'scale_group') return this._renderScaleGroup(node);
      if (t === 'yesno')       return this._renderYesNo(node);
      if (t === 'transition')  return this._renderTransition(node);
      if (t === 'upload')      return this._renderUpload(node);
      if (t === 'consent')     return this._renderConsent(node);
      if (t === 'email')       return this._renderEmail(node);
      if (t === 'loading')     return this._renderLoading(node);
      if (t === 'result')      return this._renderResult(node);
      return this._mkSlide('');
    },

    _renderIntro: function () {
      var ti = _t.intro || {};
      return this._mkSlide(
        '<div class="mps-intro-inner">' +
          '<p class="mps-intro-eyebrow">' + (ti.eyebrow || 'Swiss Excellence') + '</p>' +
          '<h1 class="mps-intro-title">' + (ti.title || 'Votre Structural<br>Aging Score\u2122') + '</h1>' +
          '<p class="mps-intro-sub">' + (ti.sub || 'Comprendre comment votre structure biologique \u00e9volue avec le temps, et comment la soutenir.') + '</p>' +
          '<p class="mps-intro-disclaimer">' + (ti.disclaimer || 'Cet outil n\u2019est pas un diagnostic m\u00e9dical. Il vise \u00e0 contextualiser votre vieillissement et \u00e0 promouvoir une approche de healthy aging.') + '</p>' +
          '<button class="mps-btn-start" type="button">' + (_ui('btn_start') || 'Commencer le test') + '</button>' +
        '</div>',
        'mps-slide--intro'
      );
    },

    _renderText: function (node) {
      var val = this.state[node.stateKey] || '';
      var dis = !val.trim() ? ' disabled' : '';
      return this._mkSlide(
        '<div class="mps-slide-inner">' +
          '<h2 class="mps-question-title">' + node.question + '</h2>' +
          '<div class="mps-input-block">' +
            '<input type="text" class="mps-text-input" id="qInput" placeholder="' + (node.placeholder || '') + '" autocomplete="given-name" value="' + this._esc(val) + '">' +
          '</div>' +
        '</div>' +
        '<div class="mps-footer">' +
          '<button class="mps-btn-continue" id="btnContinue" type="button"' + dis + '>' + (_ui('btn_continue') || 'Continuer') + '</button>' +
        '</div>'
      );
    },

    _renderNumber: function (node) {
      var val = this.state[node.stateKey];
      var hasVal = val !== null && val !== undefined && val !== '';
      var dis = !hasVal ? ' disabled' : '';
      return this._mkSlide(
        '<div class="mps-slide-inner">' +
          '<h2 class="mps-question-title">' + node.question + '</h2>' +
          '<div class="mps-number-block">' +
            '<input type="number" class="mps-number-input" id="qInput" placeholder="\u2014" min="' + node.min + '" max="' + node.max + '"' + (hasVal ? ' value="' + val + '"' : '') + '>' +
            '<span class="mps-number-unit">' + (node.unit || '') + '</span>' +
          '</div>' +
        '</div>' +
        '<div class="mps-footer">' +
          '<button class="mps-btn-continue" id="btnContinue" type="button"' + dis + '>' + (_ui('btn_continue') || 'Continuer') + '</button>' +
        '</div>'
      );
    },

    _renderSingle: function (node) {
      var self = this;
      var curVal = this.state[node.stateKey] || '';
      var isGenre = node.variant === 'genre';
      var html = '<div class="mps-slide-inner">';
      html += '<h2 class="mps-question-title">' + node.question + '</h2>';

      if (isGenre) {
        html += '<div class="mps-genre-grid">';
        node.options.forEach(function (opt) {
          var sel = curVal === opt.value ? ' selected' : '';
          var iconSvg = opt.icon === 'female'
            ? '<svg width="36" height="36" viewBox="0 0 36 36" fill="none"><circle cx="18" cy="12" r="8" stroke="currentColor" stroke-width="1.5"/><path d="M18 20v12M14 28h8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>'
            : '<svg width="36" height="36" viewBox="0 0 36 36" fill="none"><circle cx="15" cy="22" r="8" stroke="currentColor" stroke-width="1.5"/><path d="M21 15l7-7M28 8h-6M28 8v6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
          html += '<button class="mps-genre-card' + sel + '" data-value="' + opt.value + '" type="button">' +
            '<span class="mps-genre-icon">' + iconSvg + '</span>' +
            '<span class="mps-genre-label">' + opt.label + '</span>' +
          '</button>';
        });
        html += '</div>';
      } else {
        html += '<div class="mps-options">';
        node.options.forEach(function (opt) {
          var sel = curVal === opt.value ? ' selected' : '';
          var content = opt.sub
            ? '<span class="mps-option-content"><span class="mps-option-text">' + opt.label + '</span><span class="mps-option-sub">' + opt.sub + '</span></span>'
            : '<span class="mps-option-text">' + opt.label + '</span>';
          html += '<button class="mps-option' + sel + '" data-value="' + opt.value + '" type="button">' +
            content + '<span class="mps-option-radio"></span></button>';
        });
        html += '</div>';
      }

      html += '</div>';
      if (node.whyAsk) {
        html += '<div class="mps-why-ask"><p class="mps-why-ask-title">' + node.whyAsk.title + '</p><p class="mps-why-ask-text">' + node.whyAsk.text + '</p></div>';
      }
      return this._mkSlide(html);
    },

    _renderMulti: function (node) {
      var arr = this.state[node.stateKey] || [];
      var dis = arr.length === 0 ? ' disabled' : '';
      var html = '<div class="mps-slide-inner">';
      html += '<h2 class="mps-question-title">' + node.question + '</h2>';
      if (node.sub) html += '<p class="mps-question-sub">' + node.sub + '</p>';
      html += '<div class="mps-options">';
      node.options.forEach(function (opt) {
        var sel = arr.indexOf(opt.value) > -1 ? ' selected' : '';
        html += '<button class="mps-option mps-option--multi' + sel + '" data-value="' + opt.value + '" type="button">' +
          '<span class="mps-option-text">' + opt.label + '</span>' +
          '<span class="mps-option-check"><svg width="12" height="10" viewBox="0 0 12 10" fill="none"><path d="M1 5l3.5 3.5L11 1" stroke="white" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg></span>' +
        '</button>';
      });
      html += '</div></div>';
      html += '<div class="mps-footer"><button class="mps-btn-continue" id="btnContinue" type="button"' + dis + '>' + (_ui('btn_continue') || 'Continuer') + '</button></div>';
      return this._mkSlide(html);
    },

    _renderScale: function (node) {
      var val = this.state[node.stateKey];
      var html = '<div class="mps-slide-inner">';
      html += '<h2 class="mps-question-title">' + node.question + '</h2>';
      html += '<div class="mps-scale-block"><div class="mps-scale-buttons">';
      for (var i = 1; i <= 5; i++) {
        var sel = val === i ? ' selected' : '';
        html += '<button class="mps-scale-btn' + sel + '" data-value="' + i + '" type="button">' + i + '</button>';
      }
      html += '</div><div class="mps-scale-labels"><span>' + (_ui('scale_low') || 'Pas du tout') + '</span><span>' + (_ui('scale_high') || 'Tout \u00e0 fait') + '</span></div></div>';
      if (node.photoPrompt) {
        var show = val !== null && val !== undefined;
        html += '<div class="mps-photo-prompt" id="mpsPhotoPrompt" style="margin-top:32px;' + (show ? '' : 'display:none;') + '">' +
          '<p class="mps-question-sub" style="margin-bottom:14px">' + (_ui('photo_prompt') || 'Souhaitez-vous ajouter une photo de votre visage pour affiner votre score\u00a0?') + '</p>' +
          '<div class="mps-yesno-grid">' +
            '<button class="mps-genre-card' + (this.state.veut_photo === 'oui' ? ' selected' : '') + '" data-value="oui" type="button"><span class="mps-genre-label">' + (_ui('yes') || 'Oui') + '</span></button>' +
            '<button class="mps-genre-card' + (this.state.veut_photo === 'non' ? ' selected' : '') + '" data-value="non" type="button"><span class="mps-genre-label">' + (_ui('no') || 'Non') + '</span></button>' +
          '</div>' +
        '</div>';
      }
      html += '</div>';
      return this._mkSlide(html);
    },

    _renderScaleGroup: function (node) {
      var self = this;
      var allAnswered = node.items.every(function (item) { return self.state[item.stateKey] !== null && self.state[item.stateKey] !== undefined; });
      var dis = !allAnswered ? ' disabled' : '';
      var html = '<div class="mps-slide-inner">';
      html += '<h2 class="mps-question-title">' + node.groupTitle + '</h2>';
      node.items.forEach(function (item, idx) {
        var val = self.state[item.stateKey];
        html += '<div class="mps-scale-group-item" data-key="' + item.stateKey + '">';
        html += '<p class="mps-scale-group-question">' + item.question + '</p>';
        html += '<div class="mps-scale-block"><div class="mps-scale-buttons">';
        for (var i = 1; i <= 5; i++) {
          var sel = val === i ? ' selected' : '';
          html += '<button class="mps-scale-btn' + sel + '" data-value="' + i + '" data-key="' + item.stateKey + '" type="button">' + i + '</button>';
        }
        html += '</div>';
        if (idx === 0) html += '<div class="mps-scale-labels"><span>' + (_ui('scale_low') || 'Pas du tout') + '</span><span>' + (_ui('scale_high') || 'Tout \u00e0 fait') + '</span></div>';
        html += '</div></div>';
      });
      html += '</div>';
      html += '<div class="mps-footer"><button class="mps-btn-continue" id="btnContinue" type="button"' + dis + '>' + (_ui('btn_continue') || 'Continuer') + '</button></div>';
      return this._mkSlide(html);
    },

    _renderYesNo: function (node) {
      var curVal = this.state[node.stateKey] || '';
      var html = '<div class="mps-slide-inner">';
      html += '<h2 class="mps-question-title">' + node.question + '</h2>';
      html += '<div class="mps-yesno-grid">';
      ['oui', 'non'].forEach(function (v) {
        var sel = curVal === v ? ' selected' : '';
        html += '<button class="mps-genre-card' + sel + '" data-value="' + v + '" type="button">' +
          '<span class="mps-genre-label">' + (v === 'oui' ? (_ui('yes') || 'Oui') : (_ui('no') || 'Non')) + '</span></button>';
      });
      html += '</div></div>';
      return this._mkSlide(html);
    },

    _renderTransition: function (node) {
      return this._mkSlide(
        '<div class="mps-transition-content">' +
          '<p class="mps-transition-eyebrow">' + (node.eyebrow || '') + '</p>' +
          '<h2 class="mps-transition-title">' + (node.title || '') + '</h2>' +
          '<p class="mps-transition-sub">' + (node.subtitle || '') + '</p>' +
        '</div>' +
        '<div class="mps-footer">' +
          '<button class="mps-btn-continue" id="btnContinue" type="button">' + (_ui('btn_continue') || 'Continuer') + '</button>' +
        '</div>',
        'mps-slide--transition'
      );
    },

    _renderUpload: function (node) {
      var html = '<div class="mps-slide-inner">';
      html += '<h2 class="mps-question-title">' + node.question + '</h2>';
      if (node.sub) html += '<p class="mps-question-sub">' + node.sub + '</p>';
      html += '<div class="mps-upload-zone" id="mpsUploadZone" role="button" tabindex="0">' +
        '<div id="mpsUploadIcon">' +
          '<svg width="44" height="44" viewBox="0 0 44 44" fill="none" style="margin-bottom:12px"><circle cx="22" cy="22" r="21" stroke="currentColor" stroke-opacity="0.3" stroke-width="1.5"/><path d="M22 14v16M14 22h16" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/></svg>' +
          '<p class="mps-upload-text">' + (_ui('upload_cta') || 'Ajouter une photo de votre visage') + '</p>' +
          '<p class="mps-upload-hint">' + (_ui('upload_hint') || 'JPG, PNG \u00b7 max 10 Mo') + '</p>' +
        '</div>' +
        '<img id="mpsPhotoPreview" class="mps-upload-preview" src="" alt="' + (_ui('upload_preview_alt') || 'Aper\u00e7u') + '">' +
      '</div>';
      html += '<input type="file" id="mpsPhotoInput" accept="image/*" style="display:none">';
      html += '</div>';
      if (node.whyAsk) {
        html += '<div class="mps-why-ask"><p class="mps-why-ask-title">' + node.whyAsk.title + '</p><p class="mps-why-ask-text">' + node.whyAsk.text + '</p></div>';
      }
      html += '<div class="mps-footer">' +
        '<button class="mps-btn-continue" type="button">' + (_ui('btn_continue') || 'Continuer') + '</button>' +
        '<button class="mps-btn-skip" type="button">' + (_ui('btn_skip_step') || 'Passer cette \u00e9tape') + '</button>' +
      '</div>';
      return this._mkSlide(html);
    },

    _renderConsent: function () {
      var prenom = this.state.prenom;
      var cap = prenom ? prenom.charAt(0).toUpperCase() + prenom.slice(1) : '';
      var checked = this.state.consent_rgpd ? ' checked' : '';
      var dis = !this.state.consent_rgpd ? ' disabled' : '';
      var tc = _t.consent || {};
      return this._mkSlide(
        '<div class="mps-slide-inner">' +
          '<h2 class="mps-question-title">' + (tc.title_prefix || 'Bienvenue') + ' ' + this._esc(cap) + '\u00a0!</h2>' +
          '<p class="mps-consent-intro">' + (tc.intro || 'Nous allons vous poser quelques questions afin de vous proposer une s\u00e9lection de compl\u00e9ments MyPureSkin, personnalis\u00e9e selon vos besoins et vos objectifs.') + '</p>' +
          '<p class="mps-consent-disclaimer">' + (tc.disclaimer || 'Cet outil n\u2019est pas un diagnostic m\u00e9dical. Il vise \u00e0 contextualiser votre vieillissement et \u00e0 promouvoir une approche de healthy aging, globale et proactive.') + '</p>' +
          '<label class="mps-consent-label">' +
            '<input type="checkbox" id="consentCheck"' + checked + '>' +
            '<span class="mps-consent-checkmark"></span>' +
            '<span class="mps-consent-text">' + (tc.checkbox || 'En cochant cette case, j\u2019accepte que les donn\u00e9es personnelles que je fournis \u00e0 MyPureSkin soient utilis\u00e9es afin de cr\u00e9er ma recommandation personnalis\u00e9e de compl\u00e9ments.') + '</span>' +
          '</label>' +
        '</div>' +
        '<div class="mps-footer">' +
          '<button class="mps-btn-continue" id="btnContinue" type="button"' + dis + '>' + (_ui('btn_continue') || 'Continuer') + '</button>' +
        '</div>'
      );
    },

    _renderEmail: function (node) {
      var val = this.state.email || '';
      var valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
      var dis = !valid ? ' disabled' : '';
      return this._mkSlide(
        '<div class="mps-slide-inner">' +
          '<h2 class="mps-question-title">' + node.question + '</h2>' +
          '<p class="mps-question-sub">' + (node.sub || '') + '</p>' +
          '<div class="mps-input-block">' +
            '<input type="email" class="mps-text-input mps-email-input" id="qInput" placeholder="' + this._esc(_ui('email_placeholder') || 'votre@email.com') + '" autocomplete="email" value="' + this._esc(val) + '">' +
          '</div>' +
          '<label class="mps-consent-label mps-consent-label--optin">' +
            '<input type="checkbox" id="marketingCheck"' + (this.state.consent_marketing ? ' checked' : '') + '>' +
            '<span class="mps-consent-checkmark"></span>' +
            '<span class="mps-consent-text">' + (_ui('marketing_optin') || 'J\u2019accepte de recevoir par e-mail les conseils et offres de MyPureSkin. D\u00e9sinscription possible \u00e0 tout moment.') + '</span>' +
          '</label>' +
        '</div>' +
        '<div class="mps-footer">' +
          '<button class="mps-btn-continue" id="btnContinue" type="button"' + dis + '>' + (_ui('btn_see_results') || 'Voir mes r\u00e9sultats') + '</button>' +
        '</div>'
      );
    },

    _renderLoading: function () {
      var steps = (_t.loading && _t.loading.steps) || [
        'Analyse du profil biologique',
        '\u00c9valuation des facteurs de mode de vie',
        'Calcul des marqueurs cutan\u00e9s',
        'Croisement avec les donn\u00e9es cliniques',
        'Personnalisation des recommandations',
        'Score finalis\u00e9 \u2713',
      ];
      var html = '<div class="mps-loading-inner">' +
        '<p class="mps-loading-title">' + ((_t.loading && _t.loading.title) || 'Calcul de votre score<br>en cours\u2026') + '</p>' +
        '<div class="mps-loading-bar-wrapper"><div class="mps-loading-bar-fill" id="mpsLoadingBarFill"></div></div>' +
        '<div class="mps-loading-steps">';
      steps.forEach(function (s, i) {
        html += '<div class="mps-loading-step" data-step="' + i + '"><span class="mps-loading-dot"></span><span>' + s + '</span></div>';
      });
      html += '</div></div>';
      return this._mkSlide(html, 'mps-slide--loading');
    },

    _renderResult: function () {
      return this._mkSlide(
        '<div class="mps-result-inner" id="mpsResultInner">' +
          '<div id="mpsResultScore__zone"></div>' +
          '<div id="mpsRecoInner"></div>' +
          '<div class="mps-reco-cart-actions" style="display:none">' +
            '<a class="mps-reco-cart-actions__view" href="/cart">' + (_ui('btn_view_cart') || 'Voir mon panier \u2192') + '</a>' +
            '<button class="mps-reco-cart-actions__continue" type="button">' + (_ui('btn_continue_shop') || 'Continuer mes achats') + '</button>' +
          '</div>' +
          '<div class="mps-reco-restart">' +
            '<button class="mps-btn-skip" id="btnRestart" type="button">' + (_ui('btn_restart') || 'Refaire le test') + '</button>' +
          '</div>' +
        '</div>',
        'mps-slide--result'
      );
    },

    _renderPlanSelector: function (product, cardIdx) {
      var groups = product.selling_plan_groups || [];
      if (!groups.length) return '';

      var html = '<div class="mps-reco-plans" data-card="' + cardIdx + '">';

      html += '<label class="mps-reco-plans__option mps-reco-plans__option--active">';
      html += '<input type="radio" name="plan_' + cardIdx + '" value="" checked>';
      html += '<span class="mps-reco-plans__radio"></span>';
      html += '<span class="mps-reco-plans__info">';
      html += '<span class="mps-reco-plans__name">' + (_ui('plan_one_time') || 'Achat unique') + '</span>';
      html += '<span class="mps-reco-plans__price">' + this._formatMoney(product.price) + '</span>';
      html += '</span></label>';

      var self = this;
      groups.forEach(function (group) {
        group.plans.forEach(function (plan) {
          // Not sold on the product page either: gift sold out or Seal rule rejected (snippets/seal-gift-items.liquid)
          if (product.seal_gift_items && product.seal_gift_items[plan.id] === false) return;
          var planPrice = self._calcPlanPrice(product.price, plan);
          html += '<label class="mps-reco-plans__option">';
          html += '<input type="radio" name="plan_' + cardIdx + '" value="' + plan.id + '" data-plan-price="' + planPrice + '" data-plan-qty="' + self._planBoxCount(plan.name) + '">';
          html += '<span class="mps-reco-plans__radio"></span>';
          html += '<span class="mps-reco-plans__info">';
          html += '<span class="mps-reco-plans__name">' + self._esc(plan.name) + '</span>';
          html += '<span class="mps-reco-plans__price">' + self._formatMoney(planPrice) + '</span>';
          if (planPrice < product.price) {
            var pct = Math.round((1 - planPrice / product.price) * 100);
            html += '<span class="mps-reco-plans__save">-' + pct + '%</span>';
          }
          html += '</span></label>';
        });
      });

      html += '</div>';
      return html;
    },

    _calcPlanPrice: function (basePrice, plan) {
      if (plan.value_type === 'percentage') return Math.round(basePrice * (100 - plan.value) / 100);
      if (plan.value_type === 'price') return plan.value;
      if (plan.value_type === 'fixed_amount') return Math.max(0, basePrice - plan.value);
      return basePrice;
    },

    _formatMoney: function (cents) {
      var fmt = window.__shopMoneyFormat || '\u20ac{{amount}}';
      var amount = (cents / 100).toFixed(2).replace('.', ',');
      var amountNoDecimals = Math.round(cents / 100).toString();
      return fmt
        .replace('{{amount_with_comma_separator}}', amount)
        .replace('{{amount}}', amount)
        .replace('{{amount_no_decimals}}', amountNoDecimals)
        .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimals);
    },

    // Plans "N <unité> every …" (box, boîte, Packung, scatola…) : la quantité = N (même règle que BQB et le panier)
    _planBoxCount: function (planName) {
      var match = String(planName || '').match(/^\s*(\d+)\s*(box|bo\u00eete|boite|pack|kiste|dose|schachtel|scatol|confezion)/i);
      return match ? parseInt(match[1], 10) : 1;
    },

    // Erreur panier : le bouton garde un libellé court, le message Shopify (ex. stock insuffisant) s'affiche dessous
    _showCartError: function (btn, err) {
      btn.textContent = _ui('btn_error_retry') || 'Erreur, veuillez réessayer';
      var msg = err && err.fromShopify && err.message;
      var box = btn.nextElementSibling;
      if (!box || !box.classList.contains('mps-reco-error')) {
        if (!msg) return;
        box = document.createElement('p');
        box.className = 'mps-reco-error';
        box.setAttribute('role', 'alert');
        btn.insertAdjacentElement('afterend', box);
      }
      box.textContent = msg || '';
      box.hidden = !msg;
    },

    _clearCartError: function (btn) {
      var box = btn.nextElementSibling;
      if (box && box.classList.contains('mps-reco-error')) box.hidden = true;
    },

    // Préfixe de langue (/de/, /it/…) : Shopify renvoie alors ses messages d'erreur dans la langue du visiteur
    _cartUrl: function (path) {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      return root + path;
    },

    /* Seal gift line for the chosen subscription plan: the masks for 3 months, one more box of the
       product for 6 months. Resolved in Liquid with the product page menu's rules, stock included
       (snippets/seal-gift-items.liquid), with the hidden properties _seal_gift (the cart locks its
       quantity; the discount "Cadeau abonnement Seal" makes it free at every delivery) and
       _seal_gift_for (one gift line per cure). A plan the product page does not sell (gift sold
       out, or Seal rule rejected) is not offered. */
    _sealGiftItem: function (product, planId) {
      var items = product && product.seal_gift_items;
      return (planId && items && items[planId]) || null;
    },

    _addToCart: function (items, callback) {
      var self = this;
      var post = function () {
        return fetch(self._cartUrl('cart/add.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: items })
        });
      };
      var parse = function (r) {
        return r.json().catch(function () { return {}; }).then(function (data) {
          if (!r.ok) {
            // description = message localisé ; message = "Cart Error" générique, non affiché
            var error = new Error(data.description || '');
            error.fromShopify = true;
            throw error;
          }
          return data;
        });
      };

      post()
      .then(function (r) {
        // 429 : une seule relance après 1 s
        if (r.status !== 429) return parse(r);
        return new Promise(function (resolve) { setTimeout(resolve, 1000); }).then(post).then(parse);
      })
      .then(function (data) {
        // Attribut d'attribution : un échec ne doit pas afficher d'erreur (le produit est déjà ajouté)
        return fetch(self._cartUrl('cart/update.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { source: 'quiz' } })
        }).catch(function () {}).then(function () { return data; });
      })
      .then(function (data) { if (callback) callback(null, data); })
      .catch(function (err) { if (callback) callback(err); });
    },

    /* ── SLIDE FACTORY ────────────────────────────────────── */
    _mkSlide: function (innerHTML, extraClass) {
      var el = document.createElement('div');
      el.className = 'mps-slide' + (extraClass ? ' ' + extraClass : '');
      el.innerHTML = innerHTML;
      return el;
    },

    /* ── ON ENTER HOOKS ───────────────────────────────────── */
    _onEnter: function (node) {
      var self = this;
      var el = this._currentSlideEl;
      if (!el) return;

      this._bindSlideEvents(node, el);

      if (node.type === 'text' || node.type === 'number' || node.type === 'email') {
        var inp = el.querySelector('#qInput');
        if (inp) setTimeout(function () { inp.focus(); }, 360);
      }

      if (node.type === 'transition') {
        this._transitionTimer = setTimeout(function () {
          if (self.currentNodeId === node.id) self.next();
        }, 3000);
      }

      if (node.type === 'loading') {
        this._runLoading();
      }

      if (node.type === 'result') {
        this._populateResult();
        this._bindRecoEvents(el);
      }
    },

    /* ── EVENT BINDING PER SLIDE ──────────────────────────── */
    _bindSlideEvents: function (node, el) {
      var self = this;

      // Continue button
      var btnContinue = el.querySelector('#btnContinue');
      if (btnContinue) {
        btnContinue.addEventListener('click', function () { self.next(); });
      }

      // Start button
      var btnStart = el.querySelector('.mps-btn-start');
      if (btnStart) {
        btnStart.addEventListener('click', function () { self.next(); });
      }

      // Transition click-to-advance
      if (node.type === 'transition') {
        el.addEventListener('click', function () { self._clearTransitionTimer(); self.next(); });
      }

      // Text/email input
      if (node.type === 'text' || node.type === 'email') {
        var inp = el.querySelector('#qInput');
        if (inp) {
          inp.addEventListener('input', function () {
            if (node.type === 'email') {
              var valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inp.value);
              if (btnContinue) btnContinue.disabled = !valid;
              self.state[node.stateKey] = inp.value.trim();
            } else {
              if (btnContinue) btnContinue.disabled = !inp.value.trim();
              self.state[node.stateKey] = inp.value.trim();
            }
            self._saveToStorage();
          });
          inp.addEventListener('keydown', function (e) {
            if (e.key === 'Enter') self.next();
          });
        }
      }

      // Number input
      if (node.type === 'number') {
        var numInp = el.querySelector('#qInput');
        if (numInp) {
          numInp.addEventListener('input', function () {
            var v = parseInt(numInp.value || '');
            var ok = !isNaN(v) && v >= node.min && v <= node.max;
            if (btnContinue) btnContinue.disabled = !ok;
            if (ok) self.state[node.stateKey] = v;
            self._saveToStorage();
          });
          numInp.addEventListener('keydown', function (e) {
            if (e.key === 'Enter') self.next();
          });
        }
      }

      // Single select (options + genre cards)
      if (node.type === 'single' || node.type === 'yesno') {
        var btns = el.querySelectorAll('.mps-option, .mps-genre-card');
        btns.forEach(function (btn) {
          btn.addEventListener('click', function () {
            var val = btn.dataset.value;
            self.state[node.stateKey] = val;
            btns.forEach(function (b) { b.classList.remove('selected'); });
            btn.classList.add('selected');
            self._saveToStorage();
            setTimeout(function () { if (self.currentNodeId === node.id) self.next(); }, 380);
          });
        });
      }

      // Multi select
      if (node.type === 'multi') {
        var opts = el.querySelectorAll('.mps-option--multi');
        opts.forEach(function (btn) {
          btn.addEventListener('click', function () {
            var val = btn.dataset.value;
            var arr = Array.isArray(self.state[node.stateKey]) ? self.state[node.stateKey].slice() : [];
            var idx = arr.indexOf(val);
            if (idx > -1) { arr.splice(idx, 1); btn.classList.remove('selected'); }
            else { arr.push(val); btn.classList.add('selected'); }
            self.state[node.stateKey] = arr;
            if (btnContinue) btnContinue.disabled = arr.length === 0;
            self._saveToStorage();
          });
        });
      }

      // Scale (individual)
      if (node.type === 'scale') {
        var sBtns = el.querySelectorAll('.mps-scale-btn');
        sBtns.forEach(function (btn) {
          btn.addEventListener('click', function () {
            var v = parseInt(btn.dataset.value);
            self.state[node.stateKey] = v;
            sBtns.forEach(function (b) { b.classList.remove('selected'); });
            btn.classList.add('selected');
            self._saveToStorage();
            if (node.photoPrompt) {
              var prompt = el.querySelector('#mpsPhotoPrompt');
              if (prompt) prompt.style.display = '';
            } else {
              setTimeout(function () { if (self.currentNodeId === node.id) self.next(); }, 380);
            }
          });
        });
        if (node.photoPrompt) {
          el.querySelectorAll('#mpsPhotoPrompt .mps-genre-card').forEach(function (btn) {
            btn.addEventListener('click', function () {
              self.state.veut_photo = btn.dataset.value;
              el.querySelectorAll('#mpsPhotoPrompt .mps-genre-card').forEach(function (b) { b.classList.remove('selected'); });
              btn.classList.add('selected');
              self._saveToStorage();
              setTimeout(function () { if (self.currentNodeId === node.id) self.next(); }, 380);
            });
          });
        }
      }

      // Scale group
      if (node.type === 'scale_group') {
        el.querySelectorAll('.mps-scale-btn').forEach(function (btn) {
          btn.addEventListener('click', function () {
            var key = btn.dataset.key;
            var v = parseInt(btn.dataset.value);
            self.state[key] = v;
            var group = btn.closest('.mps-scale-group-item');
            if (group) group.querySelectorAll('.mps-scale-btn').forEach(function (b) { b.classList.remove('selected'); });
            btn.classList.add('selected');
            self._saveToStorage();
            var allDone = node.items.every(function (item) { return self.state[item.stateKey] !== null && self.state[item.stateKey] !== undefined; });
            if (btnContinue) btnContinue.disabled = !allDone;
          });
        });
      }

      // Opt-in marketing (étape email) : non coché par défaut, conditionne l'inscription Shopify
      if (node.type === 'email') {
        var optin = el.querySelector('#marketingCheck');
        if (optin) {
          optin.addEventListener('change', function () {
            self.state.consent_marketing = optin.checked;
            self._saveToStorage();
          });
        }
      }

      // Consent checkbox
      if (node.type === 'consent') {
        var cb = el.querySelector('#consentCheck');
        if (cb) {
          cb.addEventListener('change', function () {
            self.state.consent_rgpd = cb.checked;
            if (btnContinue) btnContinue.disabled = !cb.checked;
            self._saveToStorage();
          });
        }
      }

      // Upload
      if (node.type === 'upload') {
        var zone = el.querySelector('#mpsUploadZone');
        var fileInput = el.querySelector('#mpsPhotoInput');
        if (zone && fileInput) {
          zone.addEventListener('click', function () { fileInput.click(); });
          fileInput.addEventListener('change', function () {
            var file = fileInput.files && fileInput.files[0];
            if (!file) return;
            var reader = new FileReader();
            reader.onload = function (e) {
              self.state.photo = true;
              var preview = el.querySelector('#mpsPhotoPreview');
              var icon = el.querySelector('#mpsUploadIcon');
              if (preview) { preview.src = e.target.result; preview.style.display = 'block'; }
              if (zone) zone.classList.add('has-file');
              if (icon) icon.style.display = 'none';
              self._saveToStorage();
            };
            reader.readAsDataURL(file);
          });
        }
        var skipBtn = el.querySelector('.mps-btn-skip');
        if (skipBtn) skipBtn.addEventListener('click', function () { self.next(); });
        var contBtn = el.querySelector('.mps-btn-continue');
        if (contBtn) contBtn.addEventListener('click', function () { self.next(); });
      }

      // Result restart button
      if (node.type === 'result') {
        var restartBtn = el.querySelector('#btnRestart');
        if (restartBtn) restartBtn.addEventListener('click', function () { self.restart(); });
      }
    },

    _bindRecoEvents: function (el) {
      var self = this;
      var products = window.__quizProducts || {};
      var cartActions = el.querySelector('.mps-reco-cart-actions');

      el.querySelectorAll('.mps-reco-plans').forEach(function (plansEl) {
        plansEl.querySelectorAll('input[type="radio"]').forEach(function (radio) {
          radio.addEventListener('change', function () {
            var card = radio.closest('.mps-reco-card');
            if (!card) return;
            var handle = card.dataset.handle;
            var p = products[handle];
            if (!p) return;

            plansEl.querySelectorAll('.mps-reco-plans__option').forEach(function (opt) {
              opt.classList.remove('mps-reco-plans__option--active');
            });
            radio.closest('.mps-reco-plans__option').classList.add('mps-reco-plans__option--active');

            var priceEl = card.querySelector('.mps-reco-card__price');
            if (priceEl) {
              var newPrice = radio.value ? parseInt(radio.dataset.planPrice) : p.price;
              priceEl.textContent = self._formatMoney(newPrice);
            }
          });
        });
      });

      el.querySelectorAll('.mps-reco-card__add-btn').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var card = btn.closest('.mps-reco-card');
          if (!card) return;
          var variantId = parseInt(card.dataset.variant);
          var selectedPlan = card.querySelector('.mps-reco-plans input[type="radio"]:checked');
          var planId = selectedPlan && selectedPlan.value ? parseInt(selectedPlan.value) : null;

          var item = { id: variantId, quantity: 1 };
          if (planId) {
            item.selling_plan = planId;
            item.quantity = parseInt(selectedPlan.dataset.planQty, 10) || 1;
          }

          var cardItems = [item];
          var cardGift = self._sealGiftItem(window.__quizProducts[card.dataset.handle], planId);
          if (cardGift) cardItems.push(cardGift);

          btn.disabled = true;
          self._clearCartError(btn);
          btn.textContent = _ui('btn_adding') || 'Ajout en cours\u2026';

          self._addToCart(cardItems, function (err) {
            if (err) {
              self._showCartError(btn, err);
              btn.disabled = false;
              return;
            }
            btn.textContent = _ui('btn_added') || 'Ajout\u00e9 \u2713';
            btn.classList.add('mps-reco-card__add-btn--added');
            if (cartActions) cartActions.style.display = '';
          });
        });
      });

      var bundleBtn = el.querySelector('.mps-reco-bundle__btn');
      if (bundleBtn) {
        bundleBtn.addEventListener('click', function () {
          var items = [];
          el.querySelectorAll('.mps-reco-card').forEach(function (card) {
            // Déjà ajoutée via sa carte : ne pas la rajouter, Shopify fusionnerait les lignes (2N)
            var cardBtn = card.querySelector('.mps-reco-card__add-btn');
            if (cardBtn && cardBtn.classList.contains('mps-reco-card__add-btn--added')) return;
            var variantId = parseInt(card.dataset.variant);
            var selectedPlan = card.querySelector('.mps-reco-plans input[type="radio"]:checked');
            var planId = selectedPlan && selectedPlan.value ? parseInt(selectedPlan.value) : null;
            var item = { id: variantId, quantity: 1 };
            if (planId) {
              item.selling_plan = planId;
              item.quantity = parseInt(selectedPlan.dataset.planQty, 10) || 1;
            }
            items.push(item);
            var bundleGift = self._sealGiftItem(window.__quizProducts[card.dataset.handle], planId);
            if (bundleGift) items.push(bundleGift);
          });

          var markBundleAdded = function () {
            bundleBtn.disabled = true;
            bundleBtn.textContent = _ui('btn_bundle_added') || 'Bundle ajout\u00e9 \u2713';
            bundleBtn.classList.add('mps-reco-bundle__btn--added');
            el.querySelectorAll('.mps-reco-card__add-btn').forEach(function (b) {
              b.textContent = _ui('btn_added') || 'Ajout\u00e9 \u2713';
              b.classList.add('mps-reco-card__add-btn--added');
              b.disabled = true;
              self._clearCartError(b);
            });
            if (cartActions) cartActions.style.display = '';
          };

          if (!items.length) {
            markBundleAdded();
            return;
          }

          bundleBtn.disabled = true;
          self._clearCartError(bundleBtn);
          bundleBtn.textContent = _ui('btn_adding') || 'Ajout en cours\u2026';

          self._addToCart(items, function (err) {
            if (err) {
              self._showCartError(bundleBtn, err);
              bundleBtn.disabled = false;
              return;
            }
            markBundleAdded();
          });
        });
      }

      var continueBtn = el.querySelector('.mps-reco-cart-actions__continue');
      if (continueBtn) {
        continueBtn.addEventListener('click', function () {
          window.location.href = '/';
        });
      }

    },

    /* ── VALIDATION ───────────────────────────────────────── */
    _validate: function (node) {
      if (!node || !node.required) return true;
      var t = node.type;
      var el = this._currentSlideEl;

      if (t === 'text') {
        var inp = el && el.querySelector('#qInput');
        if (!inp || !inp.value.trim()) { this._shake(inp); return false; }
      }
      if (t === 'number') {
        var ni = el && el.querySelector('#qInput');
        var v = parseInt(ni ? ni.value : '');
        if (isNaN(v) || v < node.min || v > node.max) { this._shake(ni); return false; }
      }
      if (t === 'single' || t === 'yesno') {
        if (!this.state[node.stateKey]) return false;
      }
      if (t === 'multi') {
        var a = this.state[node.stateKey];
        if (!a || a.length === 0) return false;
      }
      if (t === 'scale') {
        if (this.state[node.stateKey] === null || this.state[node.stateKey] === undefined) return false;
        if (node.photoPrompt && !this.state.veut_photo) return false;
      }
      if (t === 'scale_group') {
        var self = this;
        if (!node.items.every(function (item) { return self.state[item.stateKey] !== null && self.state[item.stateKey] !== undefined; })) return false;
      }
      if (t === 'consent') {
        if (!this.state.consent_rgpd) return false;
      }
      if (t === 'email') {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.state.email)) return false;
      }
      return true;
    },

    /* ── SAVE NODE DATA ───────────────────────────────────── */
    _saveNode: function (node) {
      if (!node || !node.stateKey) return;
      var el = this._currentSlideEl;
      if (node.type === 'text' || node.type === 'email') {
        var inp = el && el.querySelector('#qInput');
        if (inp) this.state[node.stateKey] = inp.value.trim();
      }
      if (node.type === 'number') {
        var ni = el && el.querySelector('#qInput');
        if (ni && ni.value) this.state[node.stateKey] = parseInt(ni.value);
      }

      if (node.id === 'poids' && this.state.taille && this.state.poids) {
        var h = this.state.taille / 100;
        this.state.imc = Math.round((this.state.poids / (h * h)) * 10) / 10;
      }

        this._saveToStorage();
    },

    /* ── BRANCH EVALUATION ────────────────────────────────── */
    _evalBranch: function (branches) {
      for (var i = 0; i < branches.length; i++) {
        if (branches[i].condition(this.state)) return branches[i].next;
      }
      return branches[branches.length - 1].next;
    },

    /* ── LOADING ANIMATION ────────────────────────────────── */
    _runLoading: function () {
      var bar = document.getElementById('mpsLoadingBarFill');
      var steps = document.querySelectorAll('.mps-loading-step');
      if (bar) bar.style.width = '0%';
      steps.forEach(function (s) { s.classList.remove('visible'); });

      var schedule = [
        { delay: 350, pct: 16 },
        { delay: 900, pct: 34 },
        { delay: 1500, pct: 54 },
        { delay: 2100, pct: 72 },
        { delay: 2700, pct: 89 },
        { delay: 3250, pct: 100 },
      ];
      steps.forEach(function (step, i) {
        if (!schedule[i]) return;
        setTimeout(function () {
          step.classList.add('visible');
          if (bar) bar.style.width = schedule[i].pct + '%';
        }, schedule[i].delay);
      });

      var self = this;
      setTimeout(function () {
        self.state.reco = self._getRecommendation();
        self.state.score = self._getDisplayedScoreFromTier(self.state.reco && self.state.reco.tier);
        self._saveToStorage();
        self._saveQuizLeadToShopify();
        self._saveToGSheet();
        if (self.currentNodeId === 'loading') self.next();
      }, 4000);
    },

    /* ── RECOMMENDATION MATRIX ────────────────────────────── */
    _getRecommendation: function () {
      var genre = this.state.genre;
      var age = this.state.age || 35;
      var imc = this.state.imc || 22;

      if (genre === 'homme') {
        var older = age >= 55;
        var high = imc >= 25;
        if (!older && !high) return { products: ['MyCollagenLift'], tier: 'green', handles: ['mycollagenlift'] };
        if (!older && high)  return { products: ['MyCollagenLift', 'MyBodyContour'], tier: 'orange', handles: ['mycollagenlift', 'mybodycontour'] };
        if (older && !high)  return { products: ['MyCollagenRepair'], tier: 'orange', handles: ['mycollagenrepair'] };
        return { products: ['MyCollagenRepair', 'MyBodyContour'], tier: 'red', handles: ['mycollagenrepair', 'mybodycontour'] };
      }

      var bracket;
      if (age < 35) bracket = { threshold: 25, lowP: ['MyCollagenGlow', 'MyCollagenLift'], highP: ['MyCollagenGlow', 'MyCollagenLift', 'MyBodyContour'], lowH: ['mycollagenglow', 'mycollagenlift'], highH: ['mycollagenglow', 'mycollagenlift', 'mybodycontour'], lowTier: 'green', highTier: 'orange' };
      else if (age < 45) bracket = { threshold: 26, lowP: ['MyCollagenLift'], highP: ['MyCollagenLift', 'MyBodyContour'], lowH: ['mycollagenlift'], highH: ['mycollagenlift', 'mybodycontour'], lowTier: 'green', highTier: 'orange' };
      else if (age < 55) bracket = { threshold: 27, lowP: ['MyCollagenLift'], highP: ['MyCollagenLift', 'MyBodyContour'], lowH: ['mycollagenlift'], highH: ['mycollagenlift', 'mybodycontour'], lowTier: 'green', highTier: 'orange' };
      else bracket = { threshold: 28, lowP: ['MyCollagenRepair'], highP: ['MyCollagenRepair', 'MyBodyContour'], lowH: ['mycollagenrepair'], highH: ['mycollagenrepair', 'mybodycontour'], lowTier: 'orange', highTier: 'red' };

      if (imc < bracket.threshold) {
        return { products: bracket.lowP, tier: bracket.lowTier, handles: bracket.lowH };
      }
      return { products: bracket.highP, tier: bracket.highTier, handles: bracket.highH };
    },

    _getDisplayedScoreFromTier: function (tier) {
      if (tier === 'green') return 90;
      if (tier === 'orange') return 67;
      if (tier === 'red') return 45;
      return 67;
    },

    /* ── RESULT RENDERING ─────────────────────────────────── */
    _populateResult: function () {
      var reco = this.state.reco || this._getRecommendation();
      var score = this._getDisplayedScoreFromTier(reco && reco.tier);
      this.state.reco = reco;
      this.state.score = score;
      var prenom = this.state.prenom;
      var genre = this.state.genre;
      var cap = prenom ? prenom.charAt(0).toUpperCase() + prenom.slice(1) : '';
      var self = this;

      var tierClass = 'mps-result--' + reco.tier;
      var resultDetailsHtml = '';

      if (genre === 'homme') {
        var tatianaPhoto = 'https://cdn.shopify.com/s/files/1/0782/7380/6666/files/cropped_circle_image.png?v=1774957026';
        var menProducts = reco.products || [];
        var hasRepair = menProducts.indexOf('MyCollagenRepair') > -1;
        var hasContour = menProducts.indexOf('MyBodyContour') > -1;
        var menContent;

        if (hasRepair && hasContour) {
          menContent = {
            statusTitle: 'Structure fragilisée',
            objective: 'Soutien renforcé de la régénération et de la structure',
            interpretation: 'Votre structure biologique présente des signes de fragilisation avancée. À ce stade, l\'approche doit être globale et fonctionnelle : aller au-delà de l\'apparence pour soutenir les mécanismes de réparation, la récupération, la qualité des tissus et l\'équilibre métabolique, avec un impact direct sur la silhouette, la mobilité et la qualité de vie.',
            quote: 'À ce stade, l\'enjeu dépasse l\'apparence : il faut aussi soutenir la fonctionnalité, la récupération, la qualité des tissus et l\'équilibre métabolique.',
            quoteAuthor: 'Tatiana Brandt, co-fondatrice de MyPureSkin',
            actions: [
              'Soutenir intensivement les mécanismes biologiques de réparation',
              'Améliorer la capacité de récupération et la résilience globale',
              'Réduire l\'inflammation chronique et le stress oxydatif',
              'Préserver l\'équilibre inflammatoire',
              'Soutenir la qualité des tissus, lutter contre la cellulite et activer le drainage',
              'Favoriser un remodelage plus harmonieux de la silhouette'
            ]
          };
        } else if (hasRepair) {
          menContent = {
            statusTitle: 'Structure à soutenir',
            objective: 'Soutien renforcé de la régénération',
            interpretation: 'Votre structure biologique commence à montrer des signes de fragilisation progressive. À ce stade, l\'approche doit être globale : aller au-delà de l\'apparence pour soutenir la fonction, la capacité de récupération et la qualité des tissus, avec un impact direct sur la qualité de vie.',
            quote: 'À ce stade, l\'enjeu dépasse l\'apparence : il s\'agit aussi de soutenir la fonctionnalité, la récupération et la qualité des tissus.',
            quoteAuthor: 'Tatiana Brandt, co-fondatrice de MyPureSkin',
            actions: [
              'Soutenir intensivement les mécanismes biologiques de réparation',
              'Améliorer la capacité de récupération et la résilience globale',
              'Réduire l\'inflammation chronique et le stress oxydatif'
            ]
          };
        } else if (hasContour) {
          menContent = {
            statusTitle: 'Structure à soutenir',
            objective: 'Stabilisation & réparation',
            interpretation: 'Les mécanismes biologiques de réparation commencent à ralentir. La priorité est de stabiliser la structure existante et de soutenir activement les processus naturels de réparation.',
            quote: 'L\'organisme perd progressivement sa capacité de réparation. Les mécanismes biologiques doivent être soutenus.',
            quoteAuthor: 'Tatiana Brandt, co-fondatrice de MyPureSkin',
            actions: [
              'Ralentir l\'accélération du vieillissement structurel',
              'Soutenir la régénération tissulaire',
              'Restaurer la qualité de la peau et la résilience globale',
              'Préserver l\'équilibre inflammatoire',
              'Lutter contre la cellulite et activer le drainage'
            ]
          };
        } else {
          menContent = {
            statusTitle: 'Structure bien préservée à soutenir',
            objective: 'Prévention & maintien',
            interpretation: 'Votre structure biologique est globalement bien conservée. À ce stade, l\'enjeu n\'est pas de corriger, mais de ralentir les mécanismes de dégradation biologique liés au vieillissement, avant qu\'ils ne s\'installent.',
            quote: 'À ce stade, on ne corrige pas encore. On préserve la structure.',
            quoteAuthor: 'Tatiana Brandt, co-fondatrice de MyPureSkin',
            actions: [
              'Soutenir la densité cutanée',
              'Maintenir les mécanismes naturels de régénération',
              'Préserver l\'équilibre inflammatoire'
            ]
          };
        }

        var _tHom = _t.results && _t.results.homme;
        if (_tHom) {
          if (hasRepair && hasContour && _tHom.repair_contour) menContent = _tHom.repair_contour;
          else if (hasRepair && !hasContour && _tHom.repair)   menContent = _tHom.repair;
          else if (!hasRepair && hasContour && _tHom.contour)  menContent = _tHom.contour;
          else if (_tHom.lift)                                 menContent = _tHom.lift;
        }

        var actionIconStroke = reco.tier === 'green' ? '#2D8F4E' : (reco.tier === 'orange' ? '#D4820A' : '#C03030');

        var actionsHtml = '';
        (menContent.actions || []).forEach(function (action) {
          actionsHtml += '<li><svg width="12" height="10" viewBox="0 0 12 10" fill="none"><path d="M1 5l3.5 3.5L11 1" stroke="' + actionIconStroke + '" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg><span>' + self._esc(action) + '</span></li>';
        });

        resultDetailsHtml =
          '<div class="mps-result-interpret">' +
            '<div class="mps-result-interpret__header">' +
              '<span class="mps-result-interpret__dot ' + tierClass + '"></span>' +
              '<span class="mps-result-interpret__status">' + this._esc(menContent.statusTitle) + '</span>' +
            '</div>' +
            '<div class="mps-result-interpret__body">' +
              '<div class="mps-result-interpret__left">' +
                '<p class="mps-result-interpret__objective">' + (_ui('result_objective') || 'Objectif\u00a0:') + ' ' + this._esc(menContent.objective) + '</p>' +
                '<p class="mps-result-interpret__text">' + this._esc(menContent.interpretation) + '</p>' +
              '</div>' +
              '<div class="mps-result-interpret__right">' +
                '<img class="mps-result-interpret__photo" src="' + this._esc(tatianaPhoto) + '" alt="Tatiana Brandt" loading="lazy">' +
                '<div class="mps-result-interpret__quote">' +
                  '<p class="mps-result-interpret__quote-text">"' + this._esc(menContent.quote) + '"</p>' +
                  '<p class="mps-result-interpret__quote-author">' + this._esc(menContent.quoteAuthor) + '</p>' +
                '</div>' +
              '</div>' +
            '</div>' +
            '<div class="mps-result-interpret__actions">' +
              '<p class="mps-result-interpret__actions-label">' + (_ui('result_actions') || 'Que faire') + '</p>' +
              '<ul>' + actionsHtml + '</ul>' +
            '</div>' +
          '</div>';
      } else {
        // ── FEMME — résultat riche (même structure que HOMME) ──
        var tatianaPhotoF = 'https://cdn.shopify.com/s/files/1/0782/7380/6666/files/cropped_circle_image.png?v=1774957026';
        var femAge = this.state.age || 35;
        var femImc = this.state.imc || 22;
        var femContent;

        if (femAge < 35 && femImc < 25) {
          // ── COMBINAISON 1 : Femme < 35 ans, IMC < 25 → GLOW + LIFT ──
          femContent = {
            statusTitle: 'Structure bien préservée à soutenir',
            objective: 'Prévention & maintien',
            interpretation: 'Votre structure biologique est globalement bien conservée. À ce stade, l\'enjeu n\'est pas de corriger, mais de ralentir les mécanismes de dégradation biologique liés au vieillissement, avant qu\'ils ne s\'installent.',
            quote: 'À ce stade, on ne corrige pas encore. On préserve la structure.',
            quoteAuthor: 'Tatiana Brandt, co-fondatrice de MyPureSkin',
            actions: [
              'Soutenir la densité cutanée',
              'Maintenir les mécanismes naturels de régénération',
              'Préserver l\'équilibre inflammatoire',
            ],
          };
        } else if (femAge < 35) {
          // ── COMBINAISON 2 : Femme < 35 ans, IMC ≥ 25 → GLOW + LIFT + CONTOUR ──
          femContent = {
            statusTitle: 'Structure bien préservée à soutenir',
            objective: 'Prévention, maintien & accompagnement de la perte de poids',
            interpretation: 'Votre structure biologique est globalement bien conservée. À ce stade, il ne s\'agit pas de corriger une dégradation structurelle, mais de préserver les fondations tout en accompagnant une réduction du tissu adipeux. L\'enjeu est de soutenir les fonctions métaboliques, la qualité des tissus et la tonicité, afin de favoriser une perte de poids plus harmonieuse et durable.',
            quote: 'À ce stade, on ne corrige pas encore. On préserve la structure — tout en soutenant l\'équilibre métabolique.',
            quoteAuthor: 'Tatiana Brandt, co-fondatrice de MyPureSkin',
            actions: [
              'Préserver la densité et la qualité des tissus',
              'Maintenir les mécanismes naturels de régénération',
              'Soutenir l\'équilibre inflammatoire et métabolique',
              'Accompagner la perte de poids sans fragiliser la structure',
              'Favoriser le drainage et l\'harmonie de la silhouette',
            ],
          };
        } else if (femAge < 45 && femImc < 26) {
          // ── COMBINAISON 3 : Femme 35-44 ans, IMC < 26 → LIFT ──
          femContent = {
            statusTitle: 'Structure bien préservée à soutenir',
            objective: 'Prévention & maintien',
            interpretation: 'Votre structure biologique est globalement bien conservée. À ce stade, l\'enjeu n\'est pas de corriger, mais de ralentir les mécanismes de dégradation biologique liés au vieillissement, avant qu\'ils ne s\'installent.',
            quote: 'À ce stade, on ne corrige pas encore. On préserve la structure.',
            quoteAuthor: 'Tatiana Brandt, co-fondatrice de MyPureSkin',
            actions: [
              'Soutenir la densité cutanée',
              'Maintenir les mécanismes naturels de régénération',
              'Préserver l\'équilibre inflammatoire',
            ],
          };
        } else if (femAge < 45) {
          // ── COMBINAISON 4 : Femme 35-44 ans, IMC ≥ 26 → LIFT + CONTOUR ──
          femContent = {
            statusTitle: 'Structure bien préservée à soutenir',
            objective: 'Prévention, maintien & accompagnement de la perte de poids',
            interpretation: 'Votre structure biologique est globalement bien conservée. À ce stade, il ne s\'agit pas de corriger une dégradation structurelle, mais de préserver les fondations tout en accompagnant une réduction du tissu adipeux. L\'enjeu est de soutenir les fonctions métaboliques, la qualité des tissus et la tonicité, afin de favoriser une perte de poids plus harmonieuse et durable.',
            quote: 'À ce stade, on ne corrige pas encore. On préserve la structure — tout en soutenant l\'équilibre métabolique.',
            quoteAuthor: 'Tatiana Brandt, co-fondatrice de MyPureSkin',
            actions: [
              'Préserver la densité et la qualité des tissus',
              'Maintenir les mécanismes naturels de régénération',
              'Soutenir l\'équilibre inflammatoire et métabolique',
              'Accompagner la perte de poids sans fragiliser la structure',
              'Favoriser le drainage et l\'harmonie de la silhouette',
            ],
          };
        } else if (femAge < 55 && femImc < 27) {
          // ── COMBINAISON 5 : Femme 45-54 ans, IMC < 27 → LIFT ──
          femContent = {
            statusTitle: 'Structure bien préservée à soutenir',
            objective: 'Prévention, maintien & anticipation des changements hormonaux',
            interpretation: 'Votre structure biologique est globalement bien conservée. À ce stade, l\'enjeu n\'est pas de corriger, mais de ralentir les mécanismes de dégradation biologique liés au vieillissement, tout en anticipant les changements hormonaux associés à la péri-ménopause ou à la ménopause. Cette phase clé influence la qualité des tissus, l\'inflammation et la capacité de régénération, d\'où l\'importance d\'un soutien préventif.',
            quote: 'À ce stade, on ne corrige pas encore. On préserve la structure.',
            quoteAuthor: 'Tatiana Brandt, co-fondatrice de MyPureSkin',
            actions: [
              'Soutenir la densité et la qualité des tissus',
              'Maintenir les mécanismes naturels de régénération',
              'Préserver l\'équilibre inflammatoire',
              'Anticiper les impacts hormonaux sur la structure cutanée et tissulaire',
              'Soutenir les impacts hormonaux de la ménopause',
            ],
          };
        } else if (femAge < 55) {
          // ── COMBINAISON 6 : Femme 45-54 ans, IMC ≥ 27 → LIFT + CONTOUR ──
          femContent = {
            statusTitle: 'Structure bien préservée à soutenir',
            objective: 'Prévention, maintien & anticipation des changements hormonaux',
            interpretation: 'Votre structure biologique est globalement bien conservée. À ce stade, l\'enjeu n\'est pas de corriger, mais de ralentir les mécanismes de dégradation biologique liés au vieillissement, tout en anticipant les changements hormonaux associés à la péri-ménopause ou à la ménopause. Cette phase clé peut s\'accompagner de modifications du métabolisme, d\'une tendance à la prise de poids et de changements dans la répartition des graisses, tout en influençant la qualité des tissus, l\'inflammation et la capacité de régénération. Un soutien préventif permet de préserver la structure, l\'harmonie de la silhouette et le confort corporel.',
            quote: 'À ce stade, on ne corrige pas encore. On préserve la structure.',
            quoteAuthor: 'Tatiana Brandt, co-fondatrice de MyPureSkin',
            actions: [
              'Soutenir la densité et la qualité des tissus',
              'Maintenir les mécanismes naturels de régénération',
              'Préserver l\'équilibre inflammatoire',
              'Anticiper les impacts hormonaux sur la structure cutanée et tissulaire',
              'Accompagner l\'équilibre métabolique pour limiter la prise de poids hormonale',
              'Favoriser le drainage et limiter l\'installation de la cellulite',
            ],
          };
        } else if (femImc < 28) {
          // ── COMBINAISON 7 : Femme 55+ ans, IMC < 28 → REPAIR ──
          femContent = {
            statusTitle: 'Structure à soutenir',
            objective: 'Soutien renforcé de la régénération',
            interpretation: 'Votre structure biologique commence à montrer des signes de fragilisation progressive. À ce stade, l\'approche doit être globale : aller au-delà de l\'apparence pour soutenir la fonction, la capacité de récupération et la qualité des tissus avec un impact direct sur la qualité de vie.',
            quote: 'À ce stade, l\'enjeu dépasse l\'apparence : il s\'agit aussi de soutenir la fonctionnalité, la récupération et la qualité des tissus.',
            quoteAuthor: 'Tatiana Brandt, co-fondatrice de MyPureSkin',
            actions: [
              'Soutenir intensivement les mécanismes biologiques de réparation',
              'Améliorer la capacité de récupération et la résilience globale',
              'Réduire l\'inflammation chronique et le stress oxydatif',
            ],
          };
        } else {
          // ── COMBINAISON 8 : Femme 55+ ans, IMC ≥ 28 → REPAIR + CONTOUR ──
          femContent = {
            statusTitle: 'Structure fragilisée',
            objective: 'Soutien renforcé de la régénération et de la structure',
            interpretation: 'Votre structure biologique présente des signes de fragilisation avancée. À ce stade, l\'approche doit être globale et fonctionnelle : aller au-delà de l\'apparence pour soutenir les mécanismes de réparation, la récupération, la qualité des tissus et l\'équilibre métabolique avec un impact direct sur la silhouette, la mobilité et la qualité de vie.',
            quote: 'À ce stade, l\'enjeu dépasse l\'apparence : il faut aussi soutenir la fonctionnalité, la récupération, la qualité des tissus et l\'équilibre métabolique.',
            quoteAuthor: 'Tatiana Brandt, co-fondatrice de MyPureSkin',
            actions: [
              'Soutenir intensivement les mécanismes biologiques de réparation',
              'Améliorer la capacité de récupération et la résilience globale',
              'Réduire l\'inflammation chronique et le stress oxydatif',
              'Préserver l\'équilibre inflammatoire',
              'Soutenir la qualité des tissus, lutter contre la cellulite et activer le drainage',
              'Favoriser un remodelage plus harmonieux de la silhouette',
            ],
          };
        }

        var _femKey;
        if (femAge < 35 && femImc < 25)        _femKey = 'c1';
        else if (femAge < 35)                  _femKey = 'c2';
        else if (femAge < 45 && femImc < 26)   _femKey = 'c3';
        else if (femAge < 45)                  _femKey = 'c4';
        else if (femAge < 55 && femImc < 27)   _femKey = 'c5';
        else if (femAge < 55)                  _femKey = 'c6';
        else if (femImc < 28)                  _femKey = 'c7';
        else                                   _femKey = 'c8';
        var _tFem = _t.results && _t.results.femme;
        if (_tFem && _tFem[_femKey]) femContent = _tFem[_femKey];

        var actionIconStrokeF = reco.tier === 'green' ? '#2D8F4E' : (reco.tier === 'orange' ? '#D4820A' : '#C03030');
        var actionsHtmlF = '';
        (femContent.actions || []).forEach(function (action) {
          actionsHtmlF += '<li><svg width="12" height="10" viewBox="0 0 12 10" fill="none"><path d="M1 5l3.5 3.5L11 1" stroke="' + actionIconStrokeF + '" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg><span>' + self._esc(action) + '</span></li>';
        });

        resultDetailsHtml =
          '<div class="mps-result-interpret">' +
            '<div class="mps-result-interpret__header">' +
              '<span class="mps-result-interpret__dot ' + tierClass + '"></span>' +
              '<span class="mps-result-interpret__status">' + this._esc(femContent.statusTitle) + '</span>' +
            '</div>' +
            '<div class="mps-result-interpret__body">' +
              '<div class="mps-result-interpret__left">' +
                '<p class="mps-result-interpret__objective">' + (_ui('result_objective') || 'Objectif\u00a0:') + ' ' + this._esc(femContent.objective) + '</p>' +
                '<p class="mps-result-interpret__text">' + this._esc(femContent.interpretation) + '</p>' +
              '</div>' +
              '<div class="mps-result-interpret__right">' +
                '<img class="mps-result-interpret__photo" src="' + this._esc(tatianaPhotoF) + '" alt="Tatiana Brandt" loading="lazy">' +
                '<div class="mps-result-interpret__quote">' +
                  '<p class="mps-result-interpret__quote-text">"' + this._esc(femContent.quote) + '"</p>' +
                  '<p class="mps-result-interpret__quote-author">' + this._esc(femContent.quoteAuthor) + '</p>' +
                '</div>' +
              '</div>' +
            '</div>' +
            '<div class="mps-result-interpret__actions">' +
              '<p class="mps-result-interpret__actions-label">' + (_ui('result_actions') || 'Que faire') + '</p>' +
              '<ul>' + actionsHtmlF + '</ul>' +
            '</div>' +
          '</div>';
      }

      var prodLabels = reco.products.join(' + ');

      var scoreZone = document.getElementById('mpsResultScore__zone');
      if (!scoreZone) return;

      scoreZone.innerHTML =
        '<p class="mps-result-greeting">' + (cap ? (_ui('result_hello') || 'Bonjour') + ' ' + this._esc(cap) + ',' : (_ui('result_no_name') || 'Voici vos r\u00e9sultats,')) + '</p>' +
        '<p class="mps-result-subtitle">' + (_ui('result_subtitle') || 'Voici votre') + '</p>' +
        '<p class="mps-result-label">' + (_ui('result_label') || 'STRUCTURAL AGING SCORE\u2122') + '</p>' +
        '<div class="mps-result-score-row">' +
          '<span class="mps-result-score-number" id="mpsResultScore">0</span>' +
          '<span class="mps-result-score-total">/100</span>' +
        '</div>' +
        '<div class="mps-result-bar-wrapper">' +
          '<div class="mps-result-bar-fill ' + tierClass + '" id="mpsResultBarFill"></div>' +
        '</div>' +
        resultDetailsHtml +
        '<div class="mps-reco-badge">' +
          '<p class="mps-reco-badge-label">' + (_ui('reco_badge_label') || 'Recommandation personnalis\u00e9e') + '</p>' +
          '<p class="mps-reco-badge-products">' + prodLabels + '</p>' +
          '<p class="mps-reco-badge-sub">' + (_ui('reco_badge_sub') || 'Bas\u00e9e sur votre Structural Aging Score\u2122 et votre profil unique.') + '</p>' +
        '</div>';

      this._populateRecoCards(reco);

      var scoreEl = document.getElementById('mpsResultScore');
      if (scoreEl) {
        var current = 0;
        var step = Math.max(1, Math.ceil(score / 45));
        var counter = setInterval(function () {
          current = Math.min(current + step, score);
          scoreEl.textContent = current;
          if (current >= score) clearInterval(counter);
        }, 28);
      }

      var barEl = document.getElementById('mpsResultBarFill');
      if (barEl) {
        barEl.style.width = '0%';
        setTimeout(function () { barEl.style.width = score + '%'; }, 200);
      }
    },

    _populateRecoCards: function (reco) {
      var handles = reco.handles || [];
      var products = window.__quizProducts || {};
      var self = this;
      var container = document.getElementById('mpsRecoInner');
      if (!container) return;

      var html = '<div class="mps-reco-cards">';

      handles.forEach(function (handle, idx) {
        var p = products[handle];
        if (!p) return;

        html += '<div class="mps-reco-card" data-handle="' + handle + '" data-variant="' + p.variant_id + '">';
        html += '<div class="mps-reco-card__image-wrap">';
        html += '<img class="mps-reco-card__image" src="' + self._esc(p.image) + '" alt="' + self._esc(p.title) + '" loading="lazy">';
        html += '</div>';
        html += '<div class="mps-reco-card__body">';
        html += '<h3 class="mps-reco-card__title">' + self._esc(p.title) + '</h3>';

        var benefits = p.benefits;
        if (typeof benefits === 'string') {
          try { benefits = JSON.parse(benefits); } catch (e) { benefits = benefits.split('|'); }
        }
        if (benefits && benefits.length) {
          html += '<ul class="mps-reco-card__benefits">';
          benefits.forEach(function (b) {
            if (!b) return;
            html += '<li><svg width="12" height="10" viewBox="0 0 12 10" fill="none"><path d="M1 5l3.5 3.5L11 1" stroke="#2D8F4E" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg><span>' + self._esc(b) + '</span></li>';
          });
          html += '</ul>';
        }

        html += '<div class="mps-reco-card__price-row">';
        html += '<span class="mps-reco-card__price" data-base="' + p.price + '">' + self._formatMoney(p.price) + '</span>';
        if (p.compare_at_price && p.compare_at_price > p.price) {
          html += '<span class="mps-reco-card__compare">' + self._formatMoney(p.compare_at_price) + '</span>';
        }
        html += '</div>';

        html += self._renderPlanSelector(p, idx);

        html += '<button class="mps-reco-card__add-btn" data-handle="' + handle + '" data-variant="' + p.variant_id + '" type="button">';
        html += (_ui('btn_add_to_cart') || 'Ajouter au panier') + '</button>';
        html += '</div></div>';
      });

      html += '</div>';

      if (handles.length > 1) {
        html += '<div class="mps-reco-bundle">';
        html += '<p class="mps-reco-bundle__label">' + (_ui('bundle_label') || 'Offre combin\u00e9e') + '</p>';
        html += '<p class="mps-reco-bundle__desc">' + reco.products.join(' + ') + ' ' + (_ui('bundle_desc_suffix') || '\u2014 ajoutez les deux produits en un clic.') + '</p>';
        html += '<button class="mps-reco-bundle__btn" type="button">' + (_ui('btn_add_bundle') || 'Ajouter le bundle au panier') + '</button>';
        html += '</div>';
      }

      container.innerHTML = html;
    },

    /* ── UI UPDATES ───────────────────────────────────────── */
    _updateUI: function () {
      this._updateProgress();
      this._updateBackBtn();
      this._updateHeader();
    },

    _updateProgress: function () {
      var total = 0, done = 0;
      var found = false;
      for (var i = 0; i < NODES.length; i++) {
        var n = NODES[i];
        if (n.skipIf && n.skipIf(this.state)) continue;
        if (PROGRESS_SKIP.indexOf(n.type) > -1) continue;
        total++;
        if (!found) done++;
        if (n.id === this.currentNodeId) found = true;
      }
      var pct = total ? Math.round((done / total) * 100) : 0;
      var fill = document.getElementById('mpsProgressFill');
      if (fill) fill.style.width = pct + '%';
    },

    _updateBackBtn: function () {
      var btn = document.getElementById('mpsBackBtn');
      if (btn) {
        var node = NODE_MAP[this.currentNodeId];
        var show = this.history.length > 0 && node && ['intro', 'loading', 'result'].indexOf(node.type) === -1;
      btn.classList.toggle('visible', show);
      }

      var overlay = document.getElementById('mpsBackOverlay');
      if (overlay) {
        var n = NODE_MAP[this.currentNodeId];
        var quiz = document.getElementById('mpsQuiz');
        var headerAlways = quiz && quiz.getAttribute('data-header-always') === 'true';
        var isIntro = n && n.type === 'intro';
        var headerVisible = isIntro || headerAlways;
        var showOverlay = !headerVisible && this.history.length > 0 && n && ['intro', 'loading', 'result'].indexOf(n.type) === -1;
        overlay.classList.toggle('visible', showOverlay);
      }
    },

    _updateHeader: function () {
      var header = document.getElementById('mpsHeader');
      if (!header) return;
      var quiz = document.getElementById('mpsQuiz');
      var headerAlways = quiz && quiz.getAttribute('data-header-always') === 'true';
      var node = NODE_MAP[this.currentNodeId];
      var isIntro = node && node.type === 'intro';
      var showHeader = isIntro || headerAlways;
      header.style.opacity = showHeader ? '1' : '0';
      header.style.pointerEvents = showHeader ? 'all' : 'none';
      header.style.transition = 'opacity 0.25s ease';

    },

    /* ── HELPERS ───────────────────────────────────────────── */
    _shake: function (el) {
      if (!el) return;
      el.classList.remove('mps-shake');
      void el.offsetWidth;
      el.classList.add('mps-shake');
      el.addEventListener('animationend', function () { el.classList.remove('mps-shake'); }, { once: true });
    },

    _clearTransitionTimer: function () {
      if (this._transitionTimer) { clearTimeout(this._transitionTimer); this._transitionTimer = null; }
    },

    _esc: function (str) {
      var d = document.createElement('div');
      d.textContent = str || '';
      return d.innerHTML;
    },

    /* ── GOOGLE SHEETS PERSISTENCE ─────────────────────────── */
    _saveToGSheet: function () {
      if (!GSHEET_WEBHOOK_URL || this._gsheetSent) return;
      if (!this.state.email) return;
      this._gsheetSent = true;
      var reco = this.state.reco || this._getRecommendation();
      var displayedScore = this._getDisplayedScoreFromTier(reco && reco.tier);
      this.state.score = displayedScore;

      var niveauMap = { green: 'bon', orange: 'moyen', red: 'attention' };
      var niveau = niveauMap[reco.tier] || reco.tier || '';

      var age = this.state.age || 0;
      var tranche;
      if (age < 25) tranche = '18-24';
      else if (age < 35) tranche = '25-34';
      else if (age < 45) tranche = '35-44';
      else if (age < 55) tranche = '45-54';
      else tranche = '55+';

      var parcours = (this.state.genre || '') + ' > ' + tranche + ' > IMC:' + (this.state.imc || 'N/A');
      if (this.state.genre === 'femme' && this.state.age >= 45 && this.state.age < 55) {
        parcours += ' > ménopause:' + (this.state.menopause_concernee || 'N/A');
      }

      var payload = {
        date: new Date().toISOString(),
        email: this.state.email,
        prenom: this.state.prenom,
        genre: this.state.genre,
        age: this.state.age,
        score: displayedScore,
        score_mode: 'tier_display',
        niveau: niveau,
        products: (reco.products || []).join(', '),
        handles: (reco.handles || []).join(', '),
        parcours: parcours
      };
      try {
        fetch(GSHEET_WEBHOOK_URL, {
          method: 'POST',
          mode: 'no-cors',
          headers: { 'Content-Type': 'text/plain' },
          body: JSON.stringify(payload)
        });
      } catch (e) {}
    },

    _saveQuizLeadToShopify: function () {
      if (this._shopifyLeadSent) return;
      if (!this.state.email) return;
      // Le formulaire customer inscrit l'email au marketing : uniquement avec l'opt-in explicite
      if (!this.state.consent_marketing) return;
      var form = document.getElementById('mps-quiz-lead-form');
      if (!form) return;
      this._shopifyLeadSent = true;

      var emailInput = form.querySelector('input[name="contact[email]"]');
      if (emailInput) emailInput.value = this.state.email;

      form.setAttribute('target', 'mps-quiz-lead-iframe');

      try {
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else {
          form.submit();
        }
      } catch (e) {
        try { form.submit(); } catch (e2) {}
      }
    },

    /* ── KEYBOARD ─────────────────────────────────────────── */
    _bindGlobalEvents: function () {
      var self = this;
      var backBtn = document.getElementById('mpsBackBtn');
      if (backBtn) backBtn.addEventListener('click', function () { self.back(); });

      var backOverlay = document.getElementById('mpsBackOverlay');
      if (backOverlay) backOverlay.addEventListener('click', function () { self.back(); });

      document.addEventListener('keydown', function (e) {
        var tag = document.activeElement && document.activeElement.tagName;
        var isInput = tag === 'INPUT' || tag === 'TEXTAREA';
        var node = NODE_MAP[self.currentNodeId];
        if (!node) return;

        if (e.key === 'Enter' && !isInput) {
          var auto = ['single', 'scale', 'yesno'];
          if (auto.indexOf(node.type) === -1) { e.preventDefault(); self.next(); }
        }
        if (e.key === 'Backspace' && !isInput) { e.preventDefault(); self.back(); }
        if (e.key === 'ArrowRight' && node.type === 'transition') self.next();
        if (e.key === 'ArrowLeft' && node.type === 'transition') self.back();
      });
    },

    /* ── LOCAL STORAGE ────────────────────────────────────── */
    _saveToStorage: function () {
      try { localStorage.setItem('mps_quiz_state', JSON.stringify(this.state)); } catch (e) {}
    },
    _loadFromStorage: function () {
      try {
        var saved = localStorage.getItem('mps_quiz_state');
        if (saved) { var p = JSON.parse(saved); for (var k in p) { if (this.state.hasOwnProperty(k)) this.state[k] = p[k]; } }
      } catch (e) {}
    },
    _clearStorage: function () {
      try { localStorage.removeItem('mps_quiz_state'); } catch (e) {}
    },
  };

  /* ── BOOT ───────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { QUIZ.init(); });
  } else {
    QUIZ.init();
  }
  window.mpsQuiz = QUIZ;
})();
